/**
 * Teammate-routed send (P5.4 Agent Teams) — the `@name ` fork of sendMessage.
 *
 * A leading `@name ` matching a LIVE teammate of the open task routes this send
 * into the TEAMMATE's own thread: the normal USER_MESSAGE payload with
 * taskId = teammateTaskId (the teammate task IS addressable — server inbox +
 * sandbox actor handle it), the @name prefix stripped, an optimistic append into
 * the teammate thread, and NONE of the new-task/main-turn side effects (title
 * gen, staging tray, sidebar bump). An unknown @name is not a route — the caller
 * falls through and sends plain text to the lead.
 *
 * Lives in its own module (not teammateHandlers) for the reason browserFold
 * does: taskStore calls this from sendMessage and teammateHandlers imports
 * taskStore, so importing back would close a module cycle. The store reach and
 * the ack-timeout emit are therefore passed in rather than imported — which is
 * also the honest list of what this fork depends on.
 */

import { withSender } from "@bash/shared";
import type { FileAttachment } from "@bash/shared";
import { parseTeammateMention } from "@/lib/teammates";
import { isDesktopShell } from "@/lib/desktop-shell";
import { armPerceivedTtft } from "../helpers/perceivedTtft";
import { captureDelegationSubmitted } from "../helpers/delegationSubmitted";
import {
  findMessageByAnyId,
  markMessagePersisted,
} from "../helpers/findMessage";
import { useModeStore } from "../modeStore";
import { useRuntimeStore } from "../runtimeStore";
import {
  DEFAULT_AGENT,
  type ContentBlock,
  type ImageAttachment,
  type TaskState,
} from "../types";

/** Ack shape of a USER_MESSAGE emit — mirrors taskStore's `SendAck`. */
type SendAck = {
  success: boolean;
  error?: string;
  taskId?: string;
  messageId?: string;
};

/** The slice of taskStore this fork reaches for, structurally (no import cycle). */
type TeammateSendStore = {
  tasks: Map<string, TaskState>;
  _mutateTasks: (mutator: (tasks: Map<string, TaskState>) => void) => void;
};

export interface TeammateSendArgs {
  /** taskStore's zustand `get` — the same one sendMessage holds. */
  get: () => TeammateSendStore;
  /** taskStore's `emitUserMessage` (ack timeout ⇒ `undefined` ack). */
  emit: (
    payload: Record<string, unknown>,
    onResult: (ack?: SendAck) => void
  ) => void;
  selectedTaskId: string;
  content: string;
  images: ImageAttachment[] | undefined;
  files: FileAttachment[] | undefined;
  /** The one message identity — thread row id, wire `id`, DB row id. */
  messageId: string;
  /** The client-minted envelope uuid, minted once per send by the caller. */
  wireUuid: string;
  traceId: string;
  isDeposit: boolean;
}

/**
 * Route this send to a teammate thread if the text opens with a live
 * teammate's `@name`. Returns `true` when it routed (the caller must stop),
 * `false` when the text was not a route (the caller continues to the lead).
 */
export function routeTeammateSend(args: TeammateSendArgs): boolean {
  const {
    get,
    emit,
    selectedTaskId,
    content,
    images,
    files,
    messageId,
    wireUuid,
    traceId,
    isDeposit,
  } = args;

  const route = parseTeammateMention(content, get().tasks.get(selectedTaskId), {
    // R15 — an attachment IS the message. With a lane in view the composer
    // always prefixes its handle, so a screenshot sent with no caption is
    // `"@helper "` and nothing else; without this it routed to the LEAD while
    // the box the person typed into named the helper. Text-only sends keep the
    // old rule, so a bare typed `@name` is still words to Bash.
    allowEmptyBody: (images?.length ?? 0) > 0 || (files?.length ?? 0) > 0,
  });
  if (!route) return false;

  const teammateTask = get().tasks.get(route.teammateTaskId);
  // Perceived-TTFT: only arm when this send STARTS the teammate's turn. If
  // the teammate is mid-stream, the next delta isn't a first token — arming
  // would fire a near-zero measurement and consume the arm before the real
  // turn. Mirrors the continuation path's busy guard. (silent is already
  // false in this branch.)
  const teammateBusy =
    teammateTask?.phase === "processing" ||
    teammateTask?.phase === "interrupting";
  const teammateBlocks: ContentBlock[] = [{ type: "text", text: route.text }];
  images?.forEach((img) => {
    teammateBlocks.push({
      type: "image",
      data: img.data,
      mediaType: img.mediaType,
    });
  });
  files?.forEach((f) => {
    teammateBlocks.push({
      type: "file_attachment",
      filename: f.filename,
      url: f.url,
      mediaType: f.mediaType,
      sizeBytes: f.sizeBytes,
    });
  });
  get()._mutateTasks((tasks) => {
    const t = tasks.get(route.teammateTaskId);
    if (!t) return;
    t.messages.push({
      id: messageId,
      role: "user",
      // The person, said out loud. It used to be stamped by OMISSION — correct only
      // while "unsigned" and "the person" meant the same thing, which is precisely the
      // ambiguity this field removes. Now an unsigned row in a lane means nobody
      // recorded a sender, and the surface declines to claim one.
      content: withSender(teammateBlocks, { kind: "person" }),
      timestamp: new Date(),
      agentSlug: DEFAULT_AGENT,
      // Same two join keys the other two send paths stamp. `wireUuid` is
      // the staging namespace's key: handleStaged dedups a MESSAGE_STAGED
      // echo with findMessageByAnyId(messages, uuid), so a row without it
      // is invisible to that lookup and the echo mints a second card for a
      // message already in the thread.
      identity: { kind: "optimistic", clientId: messageId, wireUuid },
    });
    t.lastActivityAt = new Date();
    if (t.phase !== "processing") t.phase = "waiting";
    // Arms the teammate's own task, whose first agent delta lands through
    // the standard per-task handlers. Skipped for a busy/deposit send (see
    // teammateBusy above).
    if (!teammateBusy && !isDeposit) armPerceivedTtft(t, "teammate", traceId);
  });
  captureDelegationSubmitted(route.teammateTaskId, route.text, traceId, {
    silent: false,
    isDeposit,
  });
  emit(
    {
      taskId: route.teammateTaskId,
      sessionId: route.teammateTaskId,
      message: route.text,
      id: messageId,
      images,
      files,
      targetAgent: DEFAULT_AGENT,
      mode: useModeStore.getState().mode,
      runtime: teammateTask?.runtime ?? useRuntimeStore.getState().runtime,
      model: teammateTask?.model,
      // The wireUuid minted once at the top of the send — NOT a fresh mint.
      // A third uuid here is a staging key the client never recorded, so
      // the server stages under a string no thread row can be found by.
      uuid: wireUuid,
      origin: { kind: "human" as const },
      surface: isDesktopShell() ? "native" : "web",
      traceId,
    },
    (ack) => {
      // Missing ack = transport failure — same visible outcome as a rejection.
      if (!ack || !ack.success) {
        get()._mutateTasks((tasks) => {
          const t = tasks.get(route.teammateTaskId);
          if (!t) return;
          t.phase = "idle";
          const idx = findMessageByAnyId(t.messages, messageId);
          if (idx !== -1) t.messages[idx].sendFailed = true;
        });
        console.warn(
          "[sendMessage] teammate send failed:",
          ack?.error ?? "transport failure"
        );
        return;
      }
      // Reconcile optimistic → persisted (same seam as the main branches).
      if (ack?.messageId) {
        get()._mutateTasks((tasks) => {
          const t = tasks.get(route.teammateTaskId);
          if (!t) return;
          const idx = findMessageByAnyId(t.messages, messageId);
          if (idx === -1) return;
          markMessagePersisted(t.messages[idx], ack.messageId!);
        });
      }
    }
  );
  return true;
}
